import { Component, computed, inject, OnInit } from '@angular/core';
import { Router, RouterModule, RouterOutlet } from '@angular/router';
import { LayoutService } from '@core/services/layout.service';
import { AuthService } from '@core/services/auth.service';
import { APP_ROUTES, AppRole, SUPPORT_WHATSAPP_URL } from '@core/constants';
import { Button, TopBar } from '@shared';
import { Location } from '@angular/common';

export interface MenuLink {
  label: string;
  route?: string;
  children?: MenuLink[];
  expanded?: boolean;
  userHasPermission?: boolean;
}

export interface MenuItem {
  label: string;
  /** Material Symbols ligature name. */
  icon?: string;
  /** Path to an svg asset in public/, used instead of `icon`. */
  iconSrc?: string;
  /** Absolute URL. When set, the item renders as a leaf link opened in a new tab. */
  externalUrl?: string;
  children?: MenuLink[];
  expanded?: boolean;
  userHasPermission?: boolean;
}

@Component({
  selector: 'app-sidebar-layout',
  imports: [RouterOutlet, RouterModule, Button, TopBar],
  templateUrl: './sidebar-layout.html',
  styleUrl: './sidebar-layout.scss',
})
export class SidebarLayout implements OnInit {
  private readonly router = inject(Router);
  private readonly layoutService = inject(LayoutService);
  private readonly location = inject(Location);
  private readonly authService = inject(AuthService);

  actions = this.layoutService.actions;
  title = this.layoutService.title;
  subtitle = this.layoutService.subtitle;
  showGoBack = this.layoutService.showGoBack;
  goBackDefaultUrl = this.layoutService.goBackDefaultUrl;

  private canAccessReflex = this.authService.canAccessReflex();
  private canAccessReflexSettings = this.authService.canAccessReflexSettings();
  private canAccessDispatch = this.authService.canAccessDispatch();

  private logisticRoles: AppRole[] = ['admin', 'inquiry', 'supervisor'];
  private logisticSettingsRoles: AppRole[] = ['admin'];
  private canAccessLogistics = this.logisticRoles.some((role) => this.authService.hasRole(role));
  private canAccessLogisticsSettings = this.logisticSettingsRoles.some((role) =>
    this.authService.hasRole(role),
  );

  private readonly allMenuItems: MenuItem[] = [
    {
      label: 'Soporte',
      iconSrc: 'whatsapp.svg',
      externalUrl: SUPPORT_WHATSAPP_URL,
    },
    {
      label: 'Firma digital',
      icon: 'local_shipping',
      userHasPermission: this.canAccessDispatch,
      children: [
        { label: 'Entregas', route: APP_ROUTES.DISPATCH.DELIVERIES.LIST.go() },
        { label: 'Clientes', route: APP_ROUTES.DISPATCH.CLIENTS.LIST.go() },
        { label: 'Camiones', route: APP_ROUTES.DISPATCH.TRUCKS.LIST.go() },
        { label: 'Estadísticas', route: APP_ROUTES.DISPATCH.STATISTICS.ROOT.go() },
        {
          label: 'Configuración',
          children: [
            { label: 'Formularios', route: APP_ROUTES.DISPATCH.FORMS.LIST.go() },
            {
              label: 'Motivos de no entrega',
              route: APP_ROUTES.DISPATCH.NON_DELIVERY_MOTIVES.LIST.go(),
            },
            {
              label: 'Motivos de entrega parcial',
              route: APP_ROUTES.DISPATCH.PARTIAL_DELIVERY_MOTIVES.LIST.go(),
            },
            { label: 'Agrupaciones', route: APP_ROUTES.DISPATCH.GROUPINGS.LIST.go() },
            { label: 'Webhooks', route: APP_ROUTES.DISPATCH.WEBHOOKS.LIST.go() },
          ],
        },
      ],
    },
    {
      label: 'Reflex',
      icon: 'edit_note',
      userHasPermission: this.canAccessReflex,
      children: [
        {
          label: 'Estadísticas',
          route: APP_ROUTES.REFLEX.STATISTICS.go(),
          userHasPermission: this.canAccessReflex,
        },
        {
          label: 'Pruebas PVT-B',
          route: APP_ROUTES.REFLEX.ASSESSMENTS.go(),
          userHasPermission: this.canAccessReflex,
        },
        {
          label: 'Configuración',
          userHasPermission: this.canAccessReflexSettings,
          children: [
            { label: 'Parámetros', route: APP_ROUTES.REFLEX.SETTINGS.go() },
            {
              label: 'Términos y condiciones',
              route: APP_ROUTES.REFLEX.TERMS_AND_CONDITIONS.LIST.go(),
            },
            {
              label: 'Aceptaciones T&C',
              route: APP_ROUTES.REFLEX.TERMS_ACCEPTANCE.LIST.go(),
            },
          ],
        },
      ],
    },
    {
      label: 'Logística',
      icon: 'edit_note',
      userHasPermission: this.canAccessLogistics,
      children: [
        {
          label: 'Oferta Fletes',
          route: '/mantenedores/dispatchs',
          userHasPermission: this.canAccessLogistics,
        },
        {
          label: 'Notificaciones',
          route: '/mantenedores/notifications',
          userHasPermission: this.canAccessLogistics,
        },
        {
          label: 'Autorizaciones',
          route: '/mantenedores/authorizations',
          userHasPermission: this.canAccessLogistics,
        },
        {
          label: 'Configuración',
          userHasPermission: this.canAccessLogisticsSettings,
          children: [
            { label: 'Centros Logísticos', route: '/mantenedores/centers' },
            { label: 'Variables', route: '/mantenedores/settings' },
          ],
        },
      ],
    },
  ];

  /**
   * Filters menu items by checking if the user has permission to access
   * the views. It bruteforces the nesting levels from allMenuItems to avoid
   * using recursion. If allMenuItems nesting increases, then it must be
   * taken into account for the filtering.
   */
  readonly menuItems = computed<MenuItem[]>(() => {
    return this.allMenuItems
      .filter((item) => item.userHasPermission !== false)
      .map((item) => ({
        ...item,
        children: item.children?.filter((child) => child.userHasPermission !== false),
      }));
  });

  ngOnInit() {
    this.setInitialCollapseState();
  }

  /**
   * Checks the current URL on load and flags any item whose children
   * contain a matching route as expanded, so the active menu item is
   * shown open by default.
   */
  private setInitialCollapseState(): void {
    const currentUrl = this.router.url;
    this.menuItems().forEach((item) => {
      item.expanded = this.hasActiveDescendant(item.children ?? [], currentUrl);
    });
  }

  /**
   * Recursively checks whether any descendant link matches the current URL.
   * Also sets expanded on nested MenuLinks along the way.
   */
  private hasActiveDescendant(links: MenuLink[], currentUrl: string): boolean {
    return links.some((link) => {
      if (link.children?.length) {
        const childIsActive = this.hasActiveDescendant(link.children, currentUrl);
        link.expanded = childIsActive;
        return childIsActive;
      }
      return !!link.route && currentUrl.startsWith(link.route);
    });
  }

  isChildActive(route: string): boolean {
    return this.router.url.startsWith(route);
  }

  /** Toggles the expanded/collapsed state of a menu item or nested link. */
  toggleExpanded(item: MenuItem | MenuLink): void {
    item.expanded = !item.expanded;
  }

  /** Generates a unique id for a menu item's collapsible content. */
  collapseId(label: string, parentId = ''): string {
    const slug = label
      .normalize('NFD') // decompose accents: é → e + combining char
      .replace(/[\u0300-\u036f]/g, '') // strip the combining accent chars
      .toLowerCase()
      .replace(/\s+/g, '-');
    return parentId ? `${parentId}__${slug}` : slug;
  }

  /**
   * Navigates the user to the previous page inside the app.
   * `document.referrer` can't be used: in a SPA it never changes after the first load.
   * Instead the LayoutService tracks the previous in-app page, so:
   *  1. If there is a previous in-app page (different path), go back in history, which
   *     restores its URL, including the filters kept in the query params.
   *  2. Otherwise (deep link, reload, external referrer) go to defaultUrl.
   */
  goBack(e: Event) {
    e.preventDefault();

    if (!this.layoutService.previousPageUrl) {
      this.router.navigateByUrl(this.goBackDefaultUrl());
      return;
    }

    this.location.back();
  }
}
